/*
 * Aglika offers: quantity bundles + complementary add-ons on the product form.
 * Markup: snippets/aglika-offers.liquid, add-on rows: sections/aglika-addons.liquid
 */
(function () {
  const money = (cents) => (window.theme && typeof window.theme.formatMoney === 'function' ? window.theme.formatMoney(cents, window.theme.moneyFormat) : (cents / 100).toFixed(2));

  class AglikaOffers {
    constructor(root) {
      this.root = root;
      this.form = root.closest('form');
      this.wrapper = root.closest('[data-form-wrapper]') || this.form;
      this.variants = JSON.parse(root.querySelector('[data-aglika-variants]').textContent);
      this.tiers = [...root.querySelectorAll('[data-tier]')];
      this.addonsHolder = root.querySelector('[data-aglika-addons]');
      this.addonsList = root.querySelector('[data-aglika-addons-list]');
      this.total = root.querySelector('[data-aglika-total]');
      this.totalPrice = root.querySelector('[data-aglika-total-price]');
      this.dialog = this.wrapper.querySelector('[data-aglika-dialog]');
      this.addons = [];
      this.states = new Map();

      this.tiers = this.tiers.filter((tier) => this.initTier(tier));
      if (this.tiers.length) {
        this.selectTier(this.tiers[0]);
        this.onMainVariantChange();

        // Links from the offer cards (?offer=1) open the page with the offer already chosen
        const linked = new URLSearchParams(window.location.search).has('offer') && this.tiers.find((tier) => !tier.hasAttribute('data-base') && !tier.hidden);
        if (linked) this.selectTier(linked);
      }

      // Keep bundle prices and unit selects in sync with the theme's variant picker
      document.addEventListener('theme:variant:change', (event) => {
        if (event.target.contains(this.root)) setTimeout(() => this.onMainVariantChange(), 0);
      });
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[name="quantity"]')) this.updateTotal();
      });

      // Runs before the theme's own add to cart handler (bubble phase on document)
      document.addEventListener('click', (event) => this.onAddToCartClick(event), true);
      document.addEventListener('theme:cart:added', () => this.resetAddons());

      if (this.dialog) this.initDialog();
      this.loadSwatches();
      this.loadAddons();
      this.updateTotal();
    }

    /* Bundles */

    get mainVariantId() {
      return Number(this.form.querySelector('[name="id"]').value);
    }

    variantById(id) {
      return this.variants.find((variant) => variant.id === Number(id));
    }

    get selectedTier() {
      return this.tiers.find((tier) => tier.classList.contains('is-selected'));
    }

    get selectedOffer() {
      const state = this.states.get(this.selectedTier);
      return state && !state.base ? state : null;
    }

    initTier(tier) {
      const giftOff = Number(tier.dataset.giftOff ?? 100);
      const state = {
        tier,
        base: tier.hasAttribute('data-base'),
        qty: Number(tier.dataset.qty) || 1,
        off: Number(tier.dataset.off) || 0,
        lastOff: Number(tier.dataset.lastOff) || 0,
        giftOff,
        filter: tier.dataset.variantFilter || '',
        units: [],
        companion: this.initExtra(tier, '[data-tier-with]', false, tier.dataset.giftLabel),
        gift: this.initExtra(tier, '[data-tier-gift]', giftOff < 100, tier.dataset.giftLabel),
      };

      // An offer whose required product is sold out cannot be bought
      const missing = (extra) => extra && !extra.optional && !extra.options.length;
      if (missing(state.companion) || missing(state.gift)) {
        tier.remove();
        return false;
      }

      this.states.set(tier, state);
      tier.querySelector('input[type="radio"]').addEventListener('change', () => this.selectTier(tier));
      return true;
    }

    initExtra(tier, selector, optional, label) {
      const json = tier.querySelector(selector);
      if (!json) return null;

      const products = JSON.parse(json.textContent).filter(Boolean);
      const options = [];
      products.forEach((product) => {
        product.variants.forEach((variant) => {
          if (!variant.available) return;
          const plain = variant.title === 'Default Title';
          const combined = plain ? product.title : `${product.title} – ${variant.title}`;
          options.push({id: variant.id, price: variant.price, image: product.image, product: product.title, variant: plain ? '' : variant.title, label: products.length > 1 ? combined : variant.title});
        });
      });

      return {products, options, optional, label: label || '', chosen: optional ? null : options[0] || null};
    }

    tierVariants(state) {
      return state.filter ? this.variants.filter((variant) => variant.title.includes(state.filter)) : this.variants;
    }

    defaultUnitId(state) {
      const allowed = this.tierVariants(state);
      const main = allowed.find((variant) => variant.id === this.mainVariantId);
      return (main || allowed.find((variant) => variant.available) || allowed[0] || {}).id;
    }

    selectTier(tier) {
      const state = this.states.get(tier);

      this.tiers.forEach((item) => {
        const selected = item === tier;
        item.classList.toggle('is-selected', selected);
        item.querySelector('input[type="radio"]').checked = selected;
        const body = item.querySelector('[data-tier-body]');
        if (body) body.hidden = !selected;
      });
      // The theme quantity selector only applies to the single item choice
      this.wrapper.classList.toggle('aglika-offer-active', !state.base);

      state.units = Array.from({length: state.qty}, (_, index) => state.units[index] || {id: this.defaultUnitId(state), touched: false});
      this.renderBody(state);
      this.refresh();
    }

    renderBody(state) {
      const units = state.tier.querySelector('[data-tier-units]');
      const extras = state.tier.querySelector('[data-tier-extras]');
      if (!units || !extras) return;

      units.innerHTML = '';
      const allowed = this.tierVariants(state);
      if (state.qty > 1 && state.qty <= 6 && allowed.length > 1) {
        state.units.forEach((choice, index) => {
          const row = document.createElement('label');
          row.className = 'aglika-tier__unit';

          const number = document.createElement('span');
          number.textContent = `#${index + 1}`;

          const select = document.createElement('select');
          select.setAttribute('aria-label', `#${index + 1}`);
          allowed.forEach((variant) => {
            const option = new Option(variant.title, variant.id, false, variant.id === choice.id);
            option.disabled = !variant.available;
            select.add(option);
          });
          select.addEventListener('change', () => {
            choice.id = Number(select.value);
            choice.touched = true;
            this.refresh();
          });

          row.append(number, select);
          units.append(row);
        });
      }

      extras.innerHTML = '';
      if (state.companion) extras.append(this.renderExtra(state.companion, 'В комплекта', 0));
      if (state.gift && state.gift.options.length) extras.append(this.renderExtra(state.gift, state.giftOff >= 100 ? 'Подарък' : `−${state.giftOff}%`, state.giftOff));
    }

    renderExtra(extra, tag, off) {
      const row = document.createElement('div');
      row.className = 'aglika-extra';

      const image = document.createElement('img');
      image.className = 'aglika-extra__image';
      image.alt = '';

      const info = document.createElement('div');
      info.className = 'aglika-extra__info';
      const label = document.createElement('span');
      label.className = 'aglika-extra__tag';
      label.textContent = tag;
      const title = document.createElement('span');
      title.className = 'aglika-extra__title';
      info.append(label, title);

      let pick = null;
      let remove = null;
      if (extra.options.length > 1) {
        // Chosen in the popup: the model first when there are several, then its options
        const actions = document.createElement('div');
        actions.className = 'aglika-extra__actions';

        pick = document.createElement('button');
        pick.type = 'button';
        pick.className = 'aglika-extra__pick';
        pick.addEventListener('click', () => {
          this.openDialog({
            heading: `Изберете ${extra.label}`.trim(),
            products: extra.products,
            current: extra.chosen ? extra.chosen.id : null,
            off,
            onConfirm: (variant) => {
              extra.chosen = extra.options.find((option) => option.id === variant.id) || null;
              this.refresh();
            },
          });
        });
        actions.append(pick);
        image.classList.add('is-clickable');
        image.addEventListener('click', () => pick.click());

        if (extra.optional) {
          remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'aglika-extra__remove';
          remove.textContent = 'Премахни';
          remove.addEventListener('click', () => {
            extra.chosen = null;
            this.refresh();
          });
          actions.append(remove);
        }
        info.append(actions);
      } else if (extra.optional || extra.options.length > 1) {
        const select = document.createElement('select');
        select.setAttribute('aria-label', tag);
        if (extra.optional) select.add(new Option(`Изберете ${extra.label}`.trim(), '', false, !extra.chosen));
        extra.options.forEach((option) => select.add(new Option(option.label, option.id, false, extra.chosen === option)));
        select.addEventListener('change', () => {
          extra.chosen = extra.options.find((option) => option.id === Number(select.value)) || null;
          this.refresh();
        });
        info.append(select);
      }

      const price = document.createElement('span');
      price.className = 'aglika-extra__price';

      extra.elements = {row, image, title, price, pick, remove};
      row.append(image, info, price);
      return row;
    }

    updateExtra(extra, off) {
      if (!extra || !extra.elements) return;
      const {row, image, title, price, pick, remove} = extra.elements;
      // Until a model is chosen the first one stands in as the picture
      const shown = extra.chosen || extra.options[0];

      row.classList.toggle('is-empty', !extra.chosen);
      image.hidden = !(shown && shown.image);
      if (shown && shown.image) image.src = shown.image;
      title.textContent = extra.chosen ? extra.chosen.product : `Добави ${extra.label}`.trim();
      if (pick) pick.textContent = extra.chosen ? [extra.chosen.variant, 'Смени'].filter(Boolean).join(' · ') : `Изберете ${extra.label}`.trim();
      if (remove) remove.hidden = !extra.chosen;

      if (!extra.chosen) {
        price.innerHTML = '';
        return;
      }
      const discounted = Math.round((extra.chosen.price * (100 - off)) / 100);
      price.innerHTML = off > 0 ? `<strong>${discounted === 0 ? 'Безплатно' : money(discounted)}</strong><s>${money(extra.chosen.price)}</s>` : `<strong>${money(extra.chosen.price)}</strong>`;
    }

    onMainVariantChange() {
      const main = this.variantById(this.mainVariantId);

      // Offers limited to certain variants show only while one of them is picked
      this.states.forEach((state) => {
        if (state.filter) state.tier.hidden = !(main && main.title.includes(state.filter));
      });
      // The single item card on its own is not a choice
      const bundles = this.root.querySelector('[data-aglika-bundles]');
      if (bundles) bundles.hidden = !this.tiers.some((tier) => !tier.hasAttribute('data-base') && !tier.hidden);

      if (this.selectedTier && this.selectedTier.hidden) {
        this.selectTier(this.tiers[0]);
        return;
      }

      // Units the customer has not picked by hand follow the main variant
      const offer = this.selectedOffer;
      if (offer) {
        offer.units.forEach((choice) => {
          if (!choice.touched) choice.id = this.defaultUnitId(offer);
        });
        this.renderBody(offer);
      }
      this.refresh();
    }

    tierAmounts(state) {
      const selected = state.tier === this.selectedTier;
      const fallback = this.variantById(this.defaultUnitId(state));
      const prices = Array.from({length: state.qty}, (_, index) => {
        const variant = (selected && state.units[index] && this.variantById(state.units[index].id)) || fallback;
        return variant ? variant.price : 0;
      });

      let full = prices.reduce((sum, value) => sum + value, 0);
      let price = Math.round((full * (100 - state.off)) / 100);
      if (state.lastOff) price -= Math.round((Math.min(...prices) * state.lastOff) / 100);

      if (state.companion && state.companion.chosen) {
        full += state.companion.chosen.price;
        price += state.companion.chosen.price;
      }
      if (state.gift && state.gift.chosen) {
        full += state.gift.chosen.price;
        price += Math.round((state.gift.chosen.price * (100 - state.giftOff)) / 100);
      }

      return {full, price};
    }

    refresh() {
      this.states.forEach((state) => {
        const {full, price} = this.tierAmounts(state);
        state.tier.querySelector('[data-tier-price]').innerHTML = money(price);
        const compare = state.tier.querySelector('[data-tier-compare]');
        if (compare) {
          compare.innerHTML = money(full);
          compare.hidden = full === price;
        }
        this.updateExtra(state.companion, 0);
        this.updateExtra(state.gift, state.giftOff);
      });
      this.updateTotal();
    }

    /* Add-ons */

    loadAddons(intent = 'complementary') {
      const url = this.root.dataset.addonsUrl;
      if (!url || !this.addonsList) return;

      fetch(url.replace('intent=complementary', `intent=${intent}`))
        .then((response) => response.text())
        .then((html) => {
          const holder = document.createElement('div');
          holder.innerHTML = html;
          const rows = holder.querySelectorAll('[data-addon]');
          if (!rows.length) {
            // Nothing set in Search & Discovery for this product
            const fallback = this.root.dataset.addonsFallback;
            if (fallback && fallback !== intent) this.loadAddons(fallback);
            return;
          }

          rows.forEach((row) => {
            const json = row.querySelector('[data-addon-json]');
            const addon = {
              row,
              toggle: row.querySelector('[data-addon-toggle]'),
              change: row.querySelector('[data-addon-change]'),
              priceEl: row.querySelector('[data-addon-price]'),
              title: row.querySelector('[data-addon-title]').textContent,
              needsChoice: row.dataset.needsChoice === 'true',
              data: json ? JSON.parse(json.textContent) : null,
              variantId: Number(row.dataset.variantId) || null,
              price: Number(row.dataset.price) || 0,
            };
            addon.defaultPriceHtml = addon.priceEl.innerHTML;

            addon.toggle.addEventListener('change', () => this.onAddonToggle(addon));
            addon.change.addEventListener('click', () => this.openAddonDialog(addon));

            this.addons.push(addon);
            this.addonsList.append(row);
          });

          this.addonsHolder.hidden = false;
        })
        .catch((error) => console.log(error));
    }

    onAddonToggle(addon) {
      if (addon.toggle.checked && addon.needsChoice) {
        this.openAddonDialog(addon);
        return;
      }
      if (!addon.toggle.checked && addon.needsChoice) this.clearAddon(addon);

      addon.row.classList.toggle('is-selected', addon.toggle.checked);
      this.updateTotal();
    }

    clearAddon(addon) {
      addon.toggle.checked = false;
      addon.row.classList.remove('is-selected');
      if (addon.needsChoice) {
        addon.variantId = null;
        addon.price = 0;
        addon.change.hidden = true;
        addon.priceEl.innerHTML = addon.defaultPriceHtml;
      }
    }

    resetAddons() {
      this.addons.forEach((addon) => this.clearAddon(addon));
      this.updateTotal();
    }

    /* Popup: pick a model when there are several, then its options */

    // Colour names -> hex, from the theme's own assets/swatches.json
    loadSwatches() {
      this.swatches = new Map();
      const url = this.root.dataset.swatchesUrl;
      if (!url) return;

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          (data.colors || []).forEach((entry) => {
            Object.entries(entry).forEach(([name, value]) => {
              if (String(value).startsWith('#')) this.swatches.set(name.trim().toLowerCase(), value);
            });
          });
        })
        .catch((error) => console.log(error));
    }

    // A colour dot for plain colours, a thumbnail of the variant photo for patterns and designs
    dialogSwatch(optionName, optionIndex, value) {
      if (!/^(цвят|color|colour|cover color|дизайн|модел)$/i.test(optionName.trim())) return null;

      const hex = this.swatches.get(value.trim().toLowerCase());
      const photo = this.dialogSession.product.variants.find((variant) => variant.options[optionIndex] === value && variant.image);
      if (!hex && !photo) return null;

      const swatch = document.createElement('span');
      swatch.className = 'aglika-dialog__swatch';
      if (hex) {
        swatch.style.backgroundColor = hex;
      } else {
        swatch.style.backgroundImage = `url("${photo.image}")`;
      }
      return swatch;
    }

    initDialog() {
      const find = (name) => this.dialog.querySelector(`[data-aglika-dialog-${name}]`);
      this.dialogHeading = find('heading');
      this.dialogModels = find('models');
      this.dialogProduct = find('product');
      this.dialogOptions = find('options');
      this.dialogConfirm = find('confirm');
      this.dialogBack = find('back');

      find('close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        const session = this.dialogSession;
        this.dialogSession = null;
        if (session && !session.done && session.onCancel) session.onCancel();
        this.updateTotal();
      });
      this.dialogConfirm.addEventListener('click', () => this.confirmDialog());
      this.dialogBack.addEventListener('click', () => this.showDialogModels());
    }

    /**
     * @param {Object} session
     * @param {String} session.heading - Title of the model list
     * @param {Array} session.products - [{title, image, options, variants: [{id, options, price, available}]}]
     * @param {Number} session.current - Variant id picked earlier, if any
     * @param {Number} session.off - % off shown on the prices
     * @param {Function} session.onConfirm - Called with the chosen variant
     * @param {Function} session.onCancel - Called when the popup closes without a choice
     */
    openDialog(session) {
      this.dialogSession = session;
      this.dialog.showModal();

      const current = session.products.find((product) => product.variants.some((variant) => variant.id === session.current));
      if (session.products.length > 1 && !current) {
        this.showDialogModels();
      } else {
        this.showDialogProduct(current || session.products[0]);
      }
    }

    dialogPrice(price) {
      const off = this.dialogSession.off || 0;
      if (!off) return money(price);
      const discounted = Math.round((price * (100 - off)) / 100);
      return `${discounted === 0 ? 'Безплатно' : money(discounted)} <s>${money(price)}</s>`;
    }

    showDialogModels() {
      const session = this.dialogSession;
      session.product = null;

      this.dialogHeading.textContent = session.heading;
      this.dialogHeading.hidden = false;
      this.dialogModels.hidden = false;
      [this.dialogProduct, this.dialogOptions, this.dialogConfirm, this.dialogBack].forEach((element) => (element.hidden = true));

      this.dialogModels.innerHTML = '';
      session.products.forEach((product) => {
        const available = product.variants.filter((variant) => variant.available);
        if (!available.length) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'aglika-dialog__model';
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          button.append(image);
        }
        const title = document.createElement('span');
        title.className = 'aglika-dialog__model-title';
        title.textContent = product.title;
        const price = document.createElement('span');
        price.className = 'aglika-dialog__model-price';
        price.innerHTML = this.dialogPrice(Math.min(...available.map((variant) => variant.price)));
        button.append(title, price);

        button.addEventListener('click', () => this.showDialogProduct(product));
        this.dialogModels.append(button);
      });
    }

    showDialogProduct(product) {
      const session = this.dialogSession;
      session.product = product;

      // Nothing to choose on a model without options
      if (product.variants.length === 1 && session.products.length > 1) {
        this.finishDialog(product.variants[0]);
        return;
      }

      this.dialogHeading.hidden = true;
      this.dialogModels.hidden = true;
      [this.dialogProduct, this.dialogOptions, this.dialogConfirm].forEach((element) => (element.hidden = false));
      this.dialogBack.hidden = session.products.length < 2;

      const current = product.variants.find((variant) => variant.id === session.current);
      const start = current || product.variants.find((variant) => variant.available);
      this.dialogSelection = [...start.options];

      this.dialog.querySelector('[data-aglika-dialog-title]').textContent = product.title;
      const image = this.dialog.querySelector('[data-aglika-dialog-image]');
      image.hidden = !product.image;
      if (product.image) image.src = product.image;

      this.renderDialogOptions();
    }

    dialogVariant() {
      return this.dialogSession.product.variants.find((variant) => variant.options.every((value, index) => value === this.dialogSelection[index]));
    }

    renderDialogOptions() {
      const {options, variants} = this.dialogSession.product;
      this.dialogOptions.innerHTML = '';

      options.forEach((name, optionIndex) => {
        const group = document.createElement('fieldset');
        group.className = 'aglika-dialog__group';
        const legend = document.createElement('legend');
        legend.textContent = name;
        group.append(legend);

        const values = [...new Set(variants.map((variant) => variant.options[optionIndex]))];
        values.forEach((value) => {
          // Available when some in-stock variant has this value together with the other current choices
          const available = variants.some(
            (variant) => variant.available && variant.options[optionIndex] === value && variant.options.every((other, index) => index === optionIndex || other === this.dialogSelection[index])
          );
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'aglika-dialog__value';
          const swatch = this.dialogSwatch(name, optionIndex, value);
          if (swatch) button.append(swatch);
          button.append(value);
          button.disabled = !available;
          button.setAttribute('aria-pressed', this.dialogSelection[optionIndex] === value);
          button.addEventListener('click', () => {
            this.dialogSelection[optionIndex] = value;
            this.renderDialogOptions();
          });
          group.append(button);
        });

        this.dialogOptions.append(group);
      });

      const variant = this.dialogVariant();
      const photo = (variant && variant.image) || this.dialogSession.product.image;
      const image = this.dialog.querySelector('[data-aglika-dialog-image]');
      image.hidden = !photo;
      if (photo) image.src = photo;

      this.dialogConfirm.disabled = !(variant && variant.available);
      this.dialog.querySelector('[data-aglika-dialog-price]').innerHTML = variant ? this.dialogPrice(variant.price) : '';
    }

    confirmDialog() {
      const variant = this.dialogSession && this.dialogSession.product ? this.dialogVariant() : null;
      if (variant && variant.available) this.finishDialog(variant);
    }

    finishDialog(variant) {
      const session = this.dialogSession;
      session.done = true;
      session.onConfirm(variant);
      this.dialog.close();
    }

    openAddonDialog(addon) {
      this.openDialog({
        heading: '',
        products: [{title: addon.title, image: addon.data.image, options: addon.data.options, variants: addon.data.variants}],
        current: addon.variantId,
        onConfirm: (variant) => {
          addon.variantId = variant.id;
          addon.price = variant.price;
          addon.toggle.checked = true;
          addon.row.classList.add('is-selected');
          addon.change.textContent = variant.options.join(' / ');
          addon.change.hidden = false;
          addon.priceEl.innerHTML = money(variant.price);
        },
        // Closing without a choice switches the add-on back off
        onCancel: () => {
          if (!addon.variantId) this.clearAddon(addon);
        },
      });
    }

    /* Total + add to cart */

    get selectedAddons() {
      return this.addons.filter((addon) => addon.toggle.checked && addon.variantId);
    }

    get isCustomAdd() {
      return Boolean(this.selectedOffer) || this.selectedAddons.length > 0;
    }

    get formQuantity() {
      return Math.max(1, Number(this.form.querySelector('[name="quantity"]')?.value) || 1);
    }

    updateTotal() {
      if (!this.total) return;
      this.total.hidden = !this.isCustomAdd;
      if (!this.isCustomAdd) return;

      const main = this.variantById(this.mainVariantId);
      const mainAmount = this.selectedOffer ? this.tierAmounts(this.selectedOffer).price : (main ? main.price : 0) * this.formQuantity;
      const addonsAmount = this.selectedAddons.reduce((sum, addon) => sum + addon.price, 0);
      this.totalPrice.innerHTML = money(mainAmount + addonsAmount);
    }

    mainItems() {
      const offer = this.selectedOffer;
      if (!offer) return [{id: this.mainVariantId, quantity: this.formQuantity}];

      const counts = new Map();
      offer.units.forEach((choice) => counts.set(choice.id, (counts.get(choice.id) || 0) + 1));
      return [...counts].map(([id, quantity]) => ({id, quantity}));
    }

    // Products added next to the main one: the offer's companion and gift, then the add-ons
    extraItems() {
      const offer = this.selectedOffer;
      const extras = offer ? [offer.companion, offer.gift].filter((extra) => extra && extra.chosen).map((extra) => extra.chosen.id) : [];
      return [...extras, ...this.selectedAddons.map((addon) => addon.variantId)];
    }

    onAddToCartClick(event) {
      const button = event.target.closest?.('[data-add-to-cart]');
      if (!button || button.closest('form') !== this.form || !this.isCustomAdd) return;
      if (button.hasAttribute('disabled') || this.wrapper.classList.contains('variant--soldout')) return;

      const cart = window.cart;
      if (!cart || typeof cart.addToCart !== 'function') return;

      event.preventDefault();
      event.stopPropagation();

      // Line item properties and selling plan of the product form stay on the main product
      const source = new FormData(this.form);
      const data = new FormData();
      const mainItems = this.mainItems();

      mainItems.forEach((item, index) => {
        data.append(`items[${index}][id]`, item.id);
        data.append(`items[${index}][quantity]`, item.quantity);
        for (const [key, value] of source.entries()) {
          if (typeof value !== 'string' || value === '') continue;
          if (key.startsWith('properties[')) data.append(`items[${index}]${key.replace('properties[', '[properties][')}`, value);
          if (key === 'selling_plan') data.append(`items[${index}][selling_plan]`, value);
        }
      });
      this.extraItems().forEach((id, offset) => {
        const index = mainItems.length + offset;
        data.append(`items[${index}][id]`, id);
        data.append(`items[${index}][quantity]`, 1);
      });

      cart.button = button;
      cart.form = this.form;
      cart.formWrapper = this.wrapper;
      cart.showCannotAddMoreInCart = false;
      cart.addToCart(data);
    }
  }

  const init = () => {
    document.querySelectorAll('[data-aglika-offers]').forEach((root) => {
      if (root.aglikaOffers) return;
      root.aglikaOffers = new AglikaOffers(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
